<template>
	<div>
		<QuestionCard
			title="Do you want to use custom headers?"
			:description="customHeadersDescription"
			show-yes-no-buttons
			:value="useCustomHeaders"
			:disabled="!userFullPermissions"
			@input="setUseCustomHeaders"
		>
			<div
				v-if="useCustomHeaders"
				class="custom-headers-container"
			>
				<!-- Add Header Inputs -->
				<fieldset
					class="d-flex flex-row cassie-horizontal-sm header-inputs-row"
					role="group"
					aria-labelledby="custom-headers-legend"
				>
					<legend
						id="custom-headers-legend"
						class="sr-only"
					>
						Add Custom Header
					</legend>
					<TextField
						v-model="newHeaderName"
						label="Header Name"
						class="cassie-input-width-sm"
						placeholder="e.g., Authorization"
						:disabled="!userFullPermissions"
						aria-describedby="header-name-help"
					/>
					<div
						id="header-name-help"
						class="sr-only"
					>
						Enter the name of the HTTP header, such as Authorization or Content-Type
					</div>
					<TextField
						v-model="newHeaderValue"
						label="Header Value"
						class="cassie-input-width-sm"
						placeholder="e.g., Bearer [PLACEHOLDER_TOKEN]"
						:disabled="!userFullPermissions"
						aria-describedby="header-value-help"
					/>
					<div
						id="header-value-help"
						class="sr-only"
					>
						Enter the value for the header. Use placeholders like [PLACEHOLDER_TOKEN] for dynamic values
					</div>
					<PrimaryActionButton
						v-if="userFullPermissions"
						:disabled="!newHeaderName || !newHeaderValue"
						:aria-label="`Add header ${newHeaderName || 'with name'} ${newHeaderValue || 'with value'}`"
						@click="addNewHeader"
					>
						Add
					</PrimaryActionButton>
				</fieldset>
				<v-simple-table
					v-if="useCustomHeaders && editableHeaders.length > 0"
					dense
					light
					class="custom-headers-table mt-5"
					role="table"
					aria-label="Custom HTTP Headers"
				>
					<template #default>
						<thead>
							<tr role="row">
								<th
									class="text-left"
									scope="col"
									style="width: 35%;"
									role="columnheader"
									aria-sort="none"
								>
									Header Name
								</th>
								<th
									class="text-left"
									scope="col"
									style="width: 45%;"
									role="columnheader"
									aria-sort="none"
								>
									Header Value
								</th>
								<th
									v-if="userFullPermissions"
									class="text-right"
									scope="col"
									style="width: 80px"
									role="columnheader"
								>
									<span class="sr-only">
										Actions for header row
									</span>
									Action
								</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="(row, index) in editableHeaders"
								:key="index"
								class="custom-header-row"
							>
								<td
									class="custom-header-cell editable-cell text-left"
									:class="{ 'empty-cell': !row.headerName }"
									@click="focusCell($event, index, 'headerName')"
								>
									<input
										v-if="editingCell && editingCell.index === index && editingCell.field === 'headerName'"
										ref="editInput"
										v-model="row.headerName"
										class="cell-input"
										placeholder="Header name"
										:disabled="!userFullPermissions"
										@blur="stopEditing"
										@keydown.enter="stopEditing"
										@keydown.escape="cancelEditing"
										@input="handleHeaderChange(index, 'headerName', $event.target.value)"
									>
									<DropZone
										v-else
										context="header"
										:field-usage-map="fieldUsageMap"
										@field-drop="(dropData) => handleHeaderFieldDrop(dropData, index, 'headerName')"
									>
										<span class="cell-display">
											{{ row.headerName || 'Header name' }}
										</span>
									</DropZone>
								</td>
								<td
									class="custom-header-cell editable-cell text-left"
									:class="{ 'empty-cell': !row.headerValue }"
									@click="focusCell($event, index, 'headerValue')"
								>
									<input
										v-if="editingCell && editingCell.index === index && editingCell.field === 'headerValue'"
										ref="editInput"
										v-model="row.headerValue"
										class="cell-input"
										placeholder="Header value"
										:disabled="!userFullPermissions"
										@blur="stopEditing"
										@keydown.enter="stopEditing"
										@keydown.escape="cancelEditing"
										@input="handleHeaderChange(index, 'headerValue', $event.target.value)"
									>
									<DropZone
										v-else
										context="header"
										:field-usage-map="fieldUsageMap"
										@field-drop="(dropData) => handleHeaderFieldDrop(dropData, index, 'headerValue')"
									>
										<span class="cell-display">
											{{ row.headerValue || 'Header value' }}
										</span>
									</DropZone>
								</td>
								<td
									v-if="userFullPermissions"
									class="text-right"
								>
									<v-menu
										bottom
										left
										offset-y
									>
										<template #activator="{ on, attrs }">
											<v-btn
												icon
												small
												v-bind="attrs"
												v-on="on"
												@click.stop
											>
												<v-icon small>
													mdi-dots-horizontal
												</v-icon>
											</v-btn>
										</template>
										<v-list dense>
											<v-list-item @click="removeHeader(index)">
												<v-list-item-content>
													<v-list-item-title>
														Remove
													</v-list-item-title>
												</v-list-item-content>
											</v-list-item>
										</v-list>
									</v-menu>
								</td>
							</tr>
						</tbody>
					</template>
				</v-simple-table>

				<!-- Empty state message -->
				<div
					v-if="useCustomHeaders && editableHeaders.length === 0"
					class="empty-headers-message mt-2"
				>
					<p class="grey--text text--darken-1">
						No custom headers configured. Use the fields above to add your first header.
					</p>
				</div>
			</div>
		</QuestionCard>
	</div>
</template>

<script>
import { VSimpleTable, VBtn } from 'vuetify/lib/components'
import DropZone from './DropZone.vue'
import TextField from '../../../../../../../shared/components/text-field.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import { QuestionCard } from '../../../../../components/component-library-wrapper.js'

export default {
	name: 'custom-headers-table',
	components: {
		VSimpleTable,
		VBtn,
		DropZone,
		TextField,
		PrimaryActionButton,
		QuestionCard
	},
	props: {
		customHeaders: {
			type: Array,
			default: () => []
		},
		userFullPermissions: Boolean,
		fieldUsageMap: {
			type: Object,
			default: () => ({})
		},
		isNoAuthCredentials: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			editableHeaders: [],
			editingCell: null,
			originalValue: null,
			useCustomHeaders: false,
			newHeaderName: '',
			newHeaderValue: ''
		}
	},
	computed: {
		customHeadersDescription () {
			let description = 'Custom headers allow you to send additional HTTP headers with your requests. '
			if (this.isNoAuthCredentials) {
				description += 'Since this integration uses "No Auth" credentials, you may want to add custom headers for authentication (e.g., API keys, bearer tokens).'
			} else {
				description += 'This can be useful for API keys, content type specifications, or other service requirements.'
			}
			return description
		}
	},
	watch: {
		customHeaders: {
			handler (newHeaders) {
				this.initializeEditableHeaders()
				// Check if there are existing headers to determine checkbox state
				this.useCustomHeaders = newHeaders && newHeaders.length > 0
			},
			immediate: true
		}
	},
	mounted () {
		this.initializeEditableHeaders()
	},
	methods: {
		setUseCustomHeaders (value) {
			this.useCustomHeaders = value
			if (!value) {
				// Clear headers when "No" is selected
				this.$emit('update:customHeaders', [])
			} else {
				// Initialize with empty row when "Yes" is selected
				this.initializeEditableHeaders()
				// Force a re-render to ensure the table shows
				this.$forceUpdate()
			}
		},

		initializeEditableHeaders () {
			this.editableHeaders = [...(this.customHeaders || [])]
		},

		addNewHeader () {
			if (!this.newHeaderName || !this.newHeaderValue) return

			const newHeader = {
				headerName: this.newHeaderName,
				headerValue: this.newHeaderValue
			}

			this.editableHeaders.push(newHeader)
			this.emitValidHeaders()

			// Clear input fields
			this.newHeaderName = ''
			this.newHeaderValue = ''
		},

		removeHeader (index) {
			this.editableHeaders.splice(index, 1)
			this.emitValidHeaders()
		},

		handleHeaderChange (index, field, value) {
			this.editableHeaders[index][field] = value
			this.emitValidHeaders()
		},

		handleHeaderFieldDrop (dropData, index, field) {
			const fieldToken = this.generateFieldToken(dropData.field || dropData)
			this.editableHeaders[index][field] = (this.editableHeaders[index][field] || '') + fieldToken
			this.emitValidHeaders()
		},

		focusCell (event, index, field) {
			if (!this.userFullPermissions) return

			this.originalValue = this.editableHeaders[index][field]
			this.editingCell = { index, field }

			this.$nextTick(() => {
				// Safely handle this.$refs.editInput which might be undefined, an array, or a single element
				const inputs = this.$refs.editInput
				let input = null

				if (!inputs) {
					// Handle case when this.$refs.editInput is undefined or null
					console.warn('editInput ref not found')
					return
				}

				if (Array.isArray(inputs)) {
					// Handle array case - get the first valid input
					input = inputs.length > 0 ? inputs[0] : null
				} else {
					// Handle single element case
					input = inputs
				}

				// Only attempt to focus and select if we have a valid input element
				if (input && typeof input.focus === 'function') {
					try {
						input.focus()
						if (typeof input.select === 'function') {
							input.select()
						}
					} catch (error) {
						console.warn('Error focusing input:', error)
					}
				}
			})
		},

		stopEditing () {
			this.editingCell = null
			this.originalValue = null
		},

		cancelEditing () {
			if (this.editingCell && this.originalValue !== null) {
				this.editableHeaders[this.editingCell.index][this.editingCell.field] = this.originalValue
			}
			this.stopEditing()
		},

		emitValidHeaders () {
			const validHeaders = this.editableHeaders.filter(header =>
				header.headerName && header.headerName.trim() !== '' &&
				header.headerValue && header.headerValue.trim() !== ''
			)
			this.$emit('update:customHeaders', validHeaders)
		},

		generateFieldToken (field) {
			const fieldPart = field.fieldName ? field.fieldName.split('.').pop() : field
			const cleanFieldName = fieldPart.replace(/_/g, '').toUpperCase()
			return `[PLACEHOLDER_${cleanFieldName}]`
		}
	}
}
</script>

<style lang="scss" scoped>
.custom-headers-buttons {
  display: flex;
  gap: 8px;
  align-self: flex-start;
}

.custom-headers-btn {
  min-width: 60px;
  height: 32px;
}

.custom-headers-container {
  display: flex;
  flex-direction: column;
}

.header-inputs-row {
  width: calc(65% + 8px);

  .cassie-input-width-sm {
    width: auto !important;
    flex: 1;
  }
}

.empty-headers-message {
  text-align: center;
  padding: 24px;
  border: 1px dashed var(--global-border-color);
  border-radius: var(--radius, 8px);
  background-color: var(--global-main-background-color);
  width: calc(65% + 8px);

  p {
    margin: 0;
    font-size: 14px;
  }
}

.custom-headers-table {
  border: 1px solid var(--global-border-color);
  border-radius: var(--radius, 8px);
  margin-top: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  width: calc(65% + 8px) !important;
  // min-width: 900px;

  :deep(.v-data-table__wrapper) {
    border-radius: var(--radius, 8px);
  }
}

.custom-header-row {
  &:hover {
    background-color: transparent;
  }
}

.custom-header-cell {
  padding: 0 !important;
  vertical-align: middle;
  position: relative;

  &.editable-cell {
    cursor: pointer;

    &:hover:not(.editing) {
      background-color: var(--global-main-background-color);
    }

    &.empty-cell .cell-display {
      color: var(--global-muted-foreground);
      font-style: italic;
    }
  }
}

.cell-display {
  display: block;
  padding: 12px 16px;
  min-height: 20px;
  line-height: 20px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
}

.cell-input {
  width: 100%;
  border: none;
  background: transparent;
  padding: 12px 16px;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    background-color: white;
    box-shadow: inset 0 0 0 2px var(--global-color-blue-darken-2);
  }
}

.custom-header-actions {
  padding: 8px 12px !important;
  vertical-align: middle;

  .v-btn {
    margin: 0 2px;
  }
}

/* Screen reader only content */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Fieldset styling to remove default browser styles */
fieldset {
  border: none;
  margin: 0;
  padding: 0;
}

legend {
  padding: 0;
}

/* Focus styles for table cells */
.custom-header-cell:focus-within {
  outline: 2px solid var(--global-color-blue-darken-2);
  outline-offset: -2px;
}

.cell-input:focus {
  outline: 2px solid var(--global-color-blue-darken-2);
  outline-offset: -1px;
}
</style>
